"use client";

import type { IFeatureCard, INavigationItem, NavigationBarProps, IPlatformLinks, IPlatformLink } from "./types";
import { pxToRem } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { Dialog, DialogTrigger, Popover, Button as RACButton } from "react-aria-components";
import { BaseLink, type ILink, Link } from "@ui/components/core/Link";
import Image, { type StaticImageData } from "next/image";
import { NavigationMenu } from "@base-ui/react/navigation-menu";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { getImageProps } from "@utilities/payload";
import * as m from "motion/react-m";
import { Button } from "@ui/components/core/Button";
import {
  LogomarkStatic,
  LogoStatic,
  LogoStaticAgents,
  LogoStaticApi,
  LogoStaticCreative,
} from "@ui/components/core/Logo";
import imgCreative from "@ui/images/eleven-creative.png";
import imgAgents from "@ui/images/eleven-agents.png";
import imgApi from "@ui/images/eleven-api.png";
import { Noise } from "@ui/components/core/Noise/Noise";
import type { ICmsPlatform } from "@mw/shared/payload-types";
import { ListBox } from "@ui/components/core/ListBox";
import { PlatformBadge } from "./PlatformBadge";
import { isSignedIn } from "@utilities/auth";
import { getCookieKey, CookieName } from "@utilities/cookieKeys";
import { screens } from "@ui/lib/screens";
import { useHasNavigated } from "@/app/_providers/HasNavigatedProvider";
import { useMediaQuery } from "usehooks-ts";

const NAV_CTA_USER_COOKIE = getCookieKey(CookieName.User);
const NAV_HAS_NAVIGATED_COOKIE = getCookieKey(CookieName.HasNavigated);

// Builds an inline script that, before hydration, sets `data-<attr>` on its
// parent element when the given cookie is present. Used to swap navbar state
// (logged-in CTAs, minimal→full nav) without a flash, since it runs while the
// server-rendered HTML is first parsed.
const cookieAttrScript = (cookieKey: string, dataAttr: string, value: string) =>
  `(function(){var c=document.cookie;var p=document.currentScript.parentElement;if(c.indexOf('${cookieKey}=')!==-1){p.dataset.${dataAttr}='${value}';}})()`;

const NAV_CTA_SWITCH_SCRIPT = cookieAttrScript(NAV_CTA_USER_COOKIE, "navVariant", "loggedin");
const NAV_MINIMAL_SWITCH_SCRIPT = cookieAttrScript(NAV_HAS_NAVIGATED_COOKIE, "navMinimal", "navigated");

// Inlined so it survives Beasties critical-CSS extraction. The selectors don't
// match the prerendered DOM (auth state is unknown at build time, so
// data-nav-variant is always "default") and would otherwise be stripped from
// the inline <style> and only ship in the deferred sheet — causing a flash of
// the logged-out CTAs on every load for logged-in users.
const NAV_CTA_TOGGLE_CSS =
  '[data-nav-variant="loggedin"] [data-nav-cta="loggedout"]{display:none}' +
  '[data-nav-variant="loggedin"] [data-nav-cta="loggedin"]{display:contents}';

// Inlined for the same reason: the prerendered DOM only ever carries the hidden
// states ("initial"/"locked"), so the "navigated" show-rules would be stripped
// from critical CSS and flash on returning visits. The nav-links rule mirrors
// the `lg:tw-block` utility, so its breakpoint is tied to the design-system
// `lg` token rather than hardcoded.
const NAV_MINIMAL_TOGGLE_CSS =
  '[data-nav-minimal="initial"] [data-nav-minimal-item],[data-nav-minimal="locked"] [data-nav-minimal-item]{display:none}' +
  '[data-nav-minimal="navigated"] [data-nav-minimal-item="mobile-menu"]{display:contents}' +
  '[data-nav-minimal="navigated"] [data-nav-minimal-item="platform-nav"]{display:flex}' +
  '[data-nav-minimal="navigated"] [data-nav-minimal-item="nav-links"]{display:none}' +
  `@media(min-width:${screens.lg}){[data-nav-minimal="navigated"] [data-nav-minimal-item="nav-links"]{display:block}}`;

type NavCtaVariant = "default" | "loggedin";

export function NavigationBar({
  className,
  platformLinks,
  platform = null,
  logoHref = "/",
  skipLinkLabel,
  goToAppCta,
  ctas,
  inverse = false,
  loggedInCtas,
  navigationBarVariant = "default",
  nav = { primary: [], secondary: [] },
}: NavigationBarProps) {
  const t = useTranslations("ClientKeys.Globals");
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const [mobilePlatformMenuIsOpen, setMobilePlatformMenuIsOpen] = useState(false);

  const isMinimal = navigationBarVariant === "minimal";
  const isLogoOnly = navigationBarVariant === "logo-only";
  // Both variants hide the same items; "minimal" can reveal them once the
  // visitor has navigated the site, "logo-only" keeps them hidden always.
  const isCollapsible = isMinimal || isLogoOnly;

  const [navCtaVariant, setNavCtaVariant] = useState<NavCtaVariant>("default");
  useEffect(() => {
    if (isSignedIn()) {
      setNavCtaVariant("loggedin");
    }
  }, []);

  // Reactive session-navigation state (see HasNavigatedProvider). Reading it
  // here — rather than the cookie directly — means the navbar re-renders when a
  // visitor navigates into a minimal page via SPA, revealing the full nav with
  // no reload. The inline script below still handles the no-flash initial load.
  const hasNavigated = useHasNavigated();

  const mobileCtas = navCtaVariant === "loggedin" ? loggedInCtas : ctas;

  const platformLink = platform && platform !== "default" && platformLinks ? platformLinks[platform] : undefined;
  const platformLinkHref = platformLink?.href ? String(platformLink.href) : undefined;

  const hideNav = mobileMenuIsOpen || mobilePlatformMenuIsOpen;

  function getNavMinimalAttr(): string | undefined {
    if (isLogoOnly) return "locked";
    if (isMinimal) return hasNavigated ? "navigated" : "initial";
    return undefined;
  }
  const navMinimalAttr = getNavMinimalAttr();

  return (
    <div className='tw-contents' data-nav-minimal={navMinimalAttr} suppressHydrationWarning>
      {isCollapsible && <style dangerouslySetInnerHTML={{ __html: NAV_MINIMAL_TOGGLE_CSS }} />}
      {isMinimal && <script dangerouslySetInnerHTML={{ __html: NAV_MINIMAL_SWITCH_SCRIPT }} />}
      <div className='w-full'>
        <div className={clsx("tw-flex tw-h-16 tw-items-center tw-gap-x-7 xl:tw-gap-x-9 container z-50", className)}>
          <SkipToContentLink inverse={inverse} label={skipLinkLabel} />
          <div className={clsx(hideNav && "tw-opacity-0")}>
            <MainMenu
              inverse={inverse}
              nav={nav.primary}
              logoHref={logoHref}
              platform='default'
              collapsible={isCollapsible}
            />
          </div>
          <div
            className='tw-ml-auto tw-flex tw-items-center tw-justify-end tw-gap-x-4'
            data-nav-variant={navCtaVariant}
            suppressHydrationWarning
          >
            <div className='tw-contents' data-nav-cta='loggedout'>
              <CTALinks ctas={ctas} inverse={inverse} keepLastOnDesktop className={clsx(hideNav && "tw-opacity-0")} />
            </div>
            {loggedInCtas && (
              <div className='tw-hidden' data-nav-cta='loggedin'>
                <CTALinks
                  ctas={loggedInCtas}
                  inverse={inverse}
                  keepLastOnDesktop
                  className={clsx(hideNav && "tw-opacity-0")}
                />
              </div>
            )}
            <style dangerouslySetInnerHTML={{ __html: NAV_CTA_TOGGLE_CSS }} />
            <script dangerouslySetInnerHTML={{ __html: NAV_CTA_SWITCH_SCRIPT }} />
            <div className='tw-contents' data-nav-minimal-item={isCollapsible ? "mobile-menu" : undefined}>
              <MobileMenu
                ctas={mobileCtas}
                goToAppCta={goToAppCta}
                inverse={inverse}
                logoHref={logoHref}
                nav={nav.primary}
                isOpen={mobileMenuIsOpen}
                setIsOpen={setMobileMenuIsOpen}
                className='tw-mt-3.5'
                renderTopBar={({ isSubNav, ...props }) => <MobileMenuTopBar {...props} autoFocus={!isSubNav} />}
              >
                {({ ref }) => (
                  <RACButton
                    ref={ref}
                    className={clsx(
                      "tw-relative tw-rounded-full tw-outline-none tw-p-1.5 -tw-m-1.5 -tw-mr-2.5 focus-visible:tw-outline-focus lg:tw-hidden",
                      hideNav && "tw-opacity-0",
                      inverse ? "tw-text-white" : "tw-text-black",
                    )}
                    aria-label={t("navigation.open")}
                  >
                    <span className='tw-absolute -tw-inset-2.5' />
                    <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-6'>
                      <path stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' d='M4 8h16M4 16h16' />
                    </svg>
                  </RACButton>
                )}
              </MobileMenu>
            </div>
          </div>
        </div>
      </div>
      {platform && platform !== "default" && nav.secondary && nav.secondary.length > 0 && (
        <div
          className='tw-relative tw-w-full tw-h-14 tw-flex tw-items-center'
          data-nav-minimal-item={isCollapsible ? "platform-nav" : undefined}
        >
          <div
            className={clsx(
              "tw-absolute tw-z-50 tw-inset-x-0 tw-top-0 tw-h-px",
              inverse ? "tw-bg-white/10" : "bg-overlay-dark",
            )}
          />
          <div className='tw-relative tw-z-50 tw-h-9 tw-flex tw-items-center tw-justify-between tw-gap-x-0.5 container'>
            <PlatformBadge platform={platform} inverse={inverse} href={platformLinkHref} />
            <MainMenu
              inverse={inverse}
              nav={nav.secondary}
              platform={platform}
              includeLogo={false}
              align='end'
              show='md'
            />
            <MobileMenu
              ctas={mobileCtas}
              goToAppCta={goToAppCta}
              inverse={inverse}
              logoHref={logoHref}
              nav={nav.secondary}
              isOpen={mobilePlatformMenuIsOpen}
              setIsOpen={setMobilePlatformMenuIsOpen}
              isSecondary
              className='tw-mt-2.5'
              renderTopBar={({ isSubNav, ...props }) => (
                <MobileMenuTopBar {...props} offset={54} autoFocus={!isSubNav} />
              )}
            >
              {({ ref }) => (
                <RACButton
                  ref={ref}
                  className={clsx(
                    "tw-relative tw-rounded-full tw-outline-none tw-p-1.5 tw-pl-3 -tw-m-1.5 -tw-mr-2 focus-visible:tw-outline-focus md:tw-hidden",
                    "tw-flex tw-items-center tw-gap-0.5",
                    inverse ? "tw-text-white/80" : "tw-text-black/50",
                  )}
                >
                  <div className='tw-type-xs tw-font-medium'>{t("menu")}</div>
                  <span className='tw-absolute -tw-inset-2.5' />
                  <svg viewBox='0 0 24 24' fill='none' className='tw-size-5 tw-relative tw-top-0.25' aria-hidden>
                    <path
                      d='M8 10L11.2929 13.2929C11.6834 13.6834 12.3166 13.6834 12.7071 13.2929L16 10'
                      stroke='currentColor'
                      strokeWidth='1.8'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </RACButton>
              )}
            </MobileMenu>
          </div>
        </div>
      )}
    </div>
  );
}

function AdaptiveNavigationTrigger({
  children,
  rootLink,
  inverse = false,
}: {
  children: React.ReactNode;
  rootLink?: ILink | null;
  inverse?: boolean;
}) {
  // When hover is supported: hovering opens the subnav, clicking navigates to the root link.
  // When hover is not supported: tapping on the item will open the subnav only.
  const anyHover = useMediaQuery("(any-hover: hover)", { defaultValue: true });

  if (anyHover && rootLink && rootLink.href) {
    return (
      <NavigationMenu.Trigger
        nativeButton={false}
        render={
          <BaseLink
            href={rootLink.href}
            target={rootLink.target}
            rel={rootLink.rel}
            className={clsx(
              "tw-py-2 tw-px-3 tw-text-xs/5 tw-transition-colors tw-outline-none focus-visible:tw-outline-focus",
              inverse
                ? "hover:tw-bg-white/10 data-[popup-open]:tw-bg-white/10"
                : "hover:tw-bg-gray-800/5 data-[popup-open]:tw-bg-gray-800/5",
            )}
            style={{ borderRadius: pxToRem(18) }}
          >
            {children}
          </BaseLink>
        }
      />
    );
  }

  return (
    <NavigationMenu.Trigger className='tw-group tw-flex tw-items-center tw-outline-none'>
      <div
        className={clsx(
          "tw-py-2 tw-px-3 tw-text-xs/5 tw-transition-colors group-focus-visible:tw-outline-focus",
          inverse
            ? "group-hover:tw-bg-white/10 group-data-[popup-open]:tw-bg-white/10"
            : "group-hover:tw-bg-gray-800/5 group-data-[popup-open]:tw-bg-gray-800/5",
        )}
        style={{ borderRadius: pxToRem(18) }}
      >
        {children}
      </div>
    </NavigationMenu.Trigger>
  );
}

// Memoized so it doesn't re-render when the mobile menu is toggled
export const MainMenu = memo(function MainMenu({
  nav = [],
  className,
  inverse,
  platform,
  logoHref = "/",
  platformLinks,
  includeLogo = true,
  align = "start",
  show = "lg",
  closeOnScroll = false,
  collapsible = false,
}: {
  className?: string;
  inverse?: boolean;
  nav: INavigationItem[];
  platform: ICmsPlatform;
  logoHref?: string;
  platformLinks?: IPlatformLinks;
  includeLogo?: boolean;
  align?: "start" | "end";
  show?: "md" | "lg";
  closeOnScroll?: boolean;
  // When true, the nav links are tagged for the minimal/logo-only variants
  // so the inlined `data-nav-minimal` CSS can hide them. Only the primary
  // header nav opts in; the secondary platform nav is hidden as a whole.
  collapsible?: boolean;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const listInnerRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState<string | null>(null);
  const logoId = "logo";

  // If the menu is open then we close it if the list becomes hidden (i.e. on smaller screens)
  useEffect(() => {
    if (!listInnerRef.current || active === null || active === logoId) return;

    const observer = new window.ResizeObserver(([entry]) => {
      if (entry.contentBoxSize[0]?.blockSize === 0 && entry.contentBoxSize[0]?.inlineSize === 0) {
        setActive(null);
      }
    });

    observer.observe(listInnerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [active]);

  useEffect(() => {
    if (!closeOnScroll) return;

    function onScroll() {
      setActive(null);
    }

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [closeOnScroll]);

  return (
    <NavigationMenu.Root className={className} value={active} onValueChange={setActive}>
      <NavigationMenu.List
        ref={listRef}
        className={clsx("tw-flex tw-gap-x-7 xl:tw-gap-x-9", inverse ? "tw-text-white" : "tw-text-black")}
      >
        {includeLogo && (
          <NavigationMenu.Item value={logoId} className='tw-relative lg:-tw-top-0.5'>
            <Logo
              isDesktop
              platform={platform}
              href={logoHref}
              platformLinks={platformLinks}
              inverse={inverse}
              className='tw-flex-none'
            />
          </NavigationMenu.Item>
        )}
        {nav.length > 0 && (
          <li
            data-nav-minimal-item={collapsible ? "nav-links" : undefined}
            className={clsx("tw-hidden", show === "md" ? "md:tw-block" : "lg:tw-block")}
          >
            <ul ref={listInnerRef} role='list' className='tw-flex'>
              {nav.map(({ href, subNavs, subNavColumns, label, featured, rootLink }, index) => {
                const hasTwoColumnSubNavs = subNavs?.some(subNav => subNav.layout === "twoColumns");

                return (
                  <NavigationMenu.Item key={index} value={`item-${index}`} className='tw-flex'>
                    {subNavs || featured ? (
                      <>
                        <AdaptiveNavigationTrigger rootLink={rootLink} inverse={inverse}>
                          {label}
                        </AdaptiveNavigationTrigger>
                        <NavigationMenuContent>
                          {/* Sits on top of separators that shouldn't be visible, to obscure them */}
                          <div className='tw-absolute tw-z-50 tw-top-0 tw-inset-x-0 tw-bg-white tw-h-px' />

                          {subNavs && (
                            <>
                              {subNavs.map((_subNav, subNavIndex) => {
                                if (subNavIndex === 0) return null;
                                const subNavsBefore = subNavs.slice(0, subNavIndex);
                                const subNavsBeforeWidth = subNavsBefore.reduce((acc, subNav) => {
                                  return acc + (subNav.layout === "twoColumns" ? 596 : 316);
                                }, 0);
                                return (
                                  <div
                                    key={subNavIndex}
                                    className='tw-absolute tw-inset-y-0 tw-bg-gray-100 tw-w-px'
                                    style={{ left: pxToRem(subNavsBeforeWidth) }}
                                  />
                                );
                              })}
                              <ul
                                role='list'
                                className={clsx(
                                  hasTwoColumnSubNavs ? "tw-flex" : "",
                                  // Prevent column gaps
                                  "tw-text-[0px]",
                                )}
                                style={{ columns: hasTwoColumnSubNavs ? undefined : subNavColumns }}
                              >
                                {subNavs.map((subNav, subNavIndex) => (
                                  <li
                                    key={subNavIndex}
                                    className={clsx("tw-relative tw-p-4.5", subNav.label && "tw-pt-7")}
                                    style={{
                                      breakInside: "avoid",
                                      width: subNav.layout === "twoColumns" ? pxToRem(596) : pxToRem(316),
                                    }}
                                  >
                                    {/* Separator */}
                                    <div className='tw-h-px tw-bg-gray-100 tw-absolute tw-top-0 tw-inset-x-0' />

                                    {subNav.label && (
                                      <div className='tw-type-2xs tw-px-2.5 tw-font-medium tw-text-gray-500'>
                                        {subNav.label}
                                      </div>
                                    )}
                                    <ul
                                      role='list'
                                      className={clsx(
                                        "tw-grid",
                                        subNav.label && "tw-mt-5",
                                        subNav.layout === "twoColumns" ? "tw-grid-cols-2" : "tw-grid-cols-1",
                                      )}
                                    >
                                      {subNav.items?.map(item => (
                                        <li
                                          key={item.label}
                                          className='tw-relative tw-py-3 tw-px-2.5 has-[:focus-visible]:tw-outline-focus tw-rounded-xl tw-transition-colors hover:tw-bg-gray-50'
                                        >
                                          <NavigationLink
                                            href={String(item.href)}
                                            target={item.target}
                                            rel={item.rel}
                                            className='tw-block tw-type-xs tw-font-medium tw-text-black tw-outline-none'
                                          >
                                            {item.label}
                                            <span className='tw-absolute tw-inset-0' />
                                          </NavigationLink>
                                          {item.description && (
                                            <div className='tw-mt-2.5 tw-type-2xs tw-text-gray-500'>
                                              {item.description}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          {featured && <FeatureFooter {...featured} isDesktop />}
                        </NavigationMenuContent>
                      </>
                    ) : href ? (
                      <NavigationLink href={String(href)} className='tw-group tw-flex tw-items-center tw-outline-none'>
                        <div
                          className={clsx(
                            "tw-px-3 tw-py-2 tw-text-xs/5 tw-text-center tw-transition-colors group-focus-visible:tw-outline-focus",
                            inverse ? "group-hover:tw-bg-white/10" : "group-hover:tw-bg-gray-800/5",
                          )}
                          style={{ borderRadius: pxToRem(18) }}
                        >
                          {label}
                        </div>
                      </NavigationLink>
                    ) : null}
                  </NavigationMenu.Item>
                );
              })}
            </ul>
          </li>
        )}
      </NavigationMenu.List>
      <NavigationMenu.Portal>
        <NavigationMenu.Positioner
          anchor={active === logoId ? listRef : listInnerRef}
          align={align}
          collisionPadding={8}
          collisionAvoidance={{ side: "none" }}
          className={clsx(
            "tw-z-[10000] !tw-pointer-events-none tw-duration-300",
            align === "start" ? "-tw-mx-10" : "tw-ml-10",
          )}
          style={({ instant }) => ({
            marginTop: pxToRem(-30),
            width: "var(--positioner-width)",
            height: "var(--positioner-height)",
            maxWidth: "var(--available-width)",
            transitionProperty: instant ? "none" : "top, right, bottom, left",
          })}
        >
          {/* Sits between the trigger and the popover to prevent closure when moving cursor between the two */}
          <div className='tw-absolute tw-pointer-events-auto tw-inset-x-0 tw-h-2.5' style={{ top: pxToRem(30) }} />

          <NavigationMenu.Popup
            className={({ transitionStatus }) =>
              clsx(
                "tw-flex tw-pt-10 tw-px-10 tw-pb-20 tw-relative tw-duration-300",
                transitionStatus === "starting" && "-tw-translate-y-1",
              )
            }
            style={({ transitionStatus }) => ({
              width: "var(--popup-width)",
              height: "var(--popup-height)",
              transitionProperty: "opacity, transform, width, height, scale, translate, mask-position",
              maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
              maskSize: "100% 300%",
              maskPosition: transitionStatus === "starting" || transitionStatus === "ending" ? "0% 100%" : "0% 0%",
            })}
          >
            <div
              className='tw-w-full tw-bg-white tw-pointer-events-auto tw-transition tw-duration-300 tw-overflow-hidden'
              style={{
                transitionProperty: "border-radius",
                borderRadius: active === logoId ? "1rem" : "1.5rem",
                boxShadow:
                  "0 10px 20px 1px rgb(0 0 0 / 0.04), 0 48px 48px -24px rgb(0 0 0 / 0.02), 0 24px 24px -12px rgb(0 0 0 / 0.04), 0 12px 12px -6px rgb(0 0 0 / 0.04), 0 6px 6px -3px rgb(0 0 0 / 0.06), 0 3px 3px -1.5px rgb(0 0 0 / 0.06), 0 1px 1px -0.5px rgb(0 0 0 / 0.06), 0 0 0 1px rgb(0 0 0 / 0.06)",
              }}
            >
              <NavigationMenu.Viewport className='tw-relative tw-h-full tw-w-full tw-overflow-hidden' />
            </div>
          </NavigationMenu.Popup>
        </NavigationMenu.Positioner>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
});

function NavigationLink(props: NavigationMenu.Link.Props) {
  if (!props.href) {
    return null;
  }
  return <NavigationMenu.Link render={<BaseLink href={props.href} />} {...props} />;
}

interface MobileMenuTopBarContext {
  isActive: boolean;
  onClose: () => void;
  autoFocus?: boolean;
  offset?: number;
  isSubNav?: boolean;
  platform?: ICmsPlatform;
  logoHref: string;
  ctas?: ILink[];
  goToAppCta?: ILink;
  inverse?: boolean;
}

export function MobileMenu({
  nav = [],
  platform = null,
  logoHref = "/",
  ctas = [],
  inverse,
  goToAppCta,
  isOpen,
  setIsOpen,
  children,
  className,
  isSecondary = false,
  renderTopBar,
}: Pick<NavigationBarProps, "platform" | "logoHref" | "ctas" | "inverse" | "goToAppCta" | "className"> & {
  nav: INavigationItem[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  children: ({ ref, isOpen }: { ref: React.RefObject<HTMLButtonElement | null>; isOpen: boolean }) => React.ReactNode;
  isSecondary?: boolean;
  renderTopBar?: (context: MobileMenuTopBarContext) => React.ReactNode;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [activeSubNav, setActiveSubNav] = useState<number | null>(null);

  // Whether the mobile menu is exiting. Used to keep the menu open until the animation is complete.
  // https://react-spectrum.adobe.com/react-aria/styling.html#framer-motion
  const [isExiting, setIsExiting] = useState(false);

  // Set the open state while also ensuring any subnav is closed, and that the `isExiting` state is updated.
  const toggle = useCallback(
    function toggle(isOpen: boolean, { instant = false }: { instant?: boolean } = {}) {
      if (isOpen) {
        setIsOpen(true);
      } else {
        if (!instant) {
          setIsExiting(true);
        }
        setIsOpen(false);
        setActiveSubNav(null);
      }
    },
    [setIsOpen, setActiveSubNav, setIsExiting],
  );

  // If the mobile menu is open then we close it if the toggle button becomes hidden (i.e. on larger screens)
  useEffect(() => {
    if (!triggerRef.current || !isOpen) return;

    const observer = new window.ResizeObserver(([entry]) => {
      if (entry.contentBoxSize[0]?.blockSize === 0 && entry.contentBoxSize[0]?.inlineSize === 0) {
        toggle(false, { instant: true });
      }
    });

    observer.observe(triggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isOpen, toggle]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={toggle}>
      {children({ ref: triggerRef, isOpen })}
      <Popover
        placement='bottom right'
        containerPadding={0}
        offset={0}
        className={clsx("tw-h-screen tw-w-full tw-flex", className)}
        isExiting={isExiting}
      >
        <Dialog className='tw-relative tw-w-full tw-flex'>
          {renderTopBar &&
            renderTopBar({
              platform,
              logoHref,
              isActive: activeSubNav === null && !isExiting,
              onClose: () => toggle(false),
              ctas,
              goToAppCta,
              inverse,
            })}
          <m.div
            className='tw-w-full tw-flex tw-p-1 -tw-mt-1'
            style={{
              maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
              maskSize: "100% 300%",
            }}
            initial={{ maskPosition: "0% 100%" }}
            animate={isOpen ? { maskPosition: "0% 0%" } : { maskPosition: "0% 100%" }}
            onAnimationComplete={isOpen ? undefined : () => setIsExiting(false)}
          >
            <nav
              className='tw-w-full tw-flex tw-bg-white tw-rounded-[1.25rem]'
              style={{
                boxShadow:
                  "0 10px 20px 1px rgb(0 0 0 / 0.04), 0 48px 48px -24px rgb(0 0 0 / 0.02), 0 24px 24px -12px rgb(0 0 0 / 0.04), 0 12px 12px -6px rgb(0 0 0 / 0.04), 0 6px 6px -3px rgb(0 0 0 / 0.06), 0 3px 3px -1.5px rgb(0 0 0 / 0.06), 0 1px 1px -0.5px rgb(0 0 0 / 0.06), 0 0 0 1px rgb(0 0 0 / 0.06)",
              }}
            >
              <m.div
                className='tw-w-full tw-flex tw-flex-col'
                initial={{ opacity: 1 }}
                animate={{ opacity: activeSubNav === null && !isExiting ? 1 : 0 }}
              >
                <ul
                  role='list'
                  className='tw-p-2 tw-flex-auto tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-gray-200'
                >
                  {nav.map(({ href, label, subNavs, featured }, index) => (
                    <li key={index}>
                      {subNavs && subNavs.length > 0 ? (
                        <MobileSubNav
                          ctas={ctas}
                          goToAppCta={goToAppCta}
                          inverse={inverse}
                          logoHref={logoHref}
                          label={label}
                          subNavs={subNavs}
                          featured={featured}
                          isOpen={activeSubNav === index}
                          setIsOpen={isOpen => setActiveSubNav(isOpen ? index : null)}
                          closeMenu={() => toggle(false)}
                          triggerRef={triggerRef}
                          isMenuExiting={isExiting}
                          isSecondary={isSecondary}
                          renderTopBar={renderTopBar}
                          className={className}
                        />
                      ) : (
                        <BaseLink
                          href={String(href)}
                          className='tw-block tw-w-full tw-text-left tw-text-xs/5 tw-font-medium tw-text-black tw-px-4 tw-py-3 tw-rounded-xl tw-transition-colors hover:tw-bg-gray-100 tw-outline-none focus-visible:tw-outline-focus'
                        >
                          {label}
                        </BaseLink>
                      )}
                    </li>
                  ))}
                </ul>
                {ctas && ctas.length > 0 && (
                  <div className='tw-border-t tw-border-gray-100 tw-p-5'>
                    <CTALinks ctas={ctas} goToAppCta={goToAppCta} buttonGrow buttonSize='md' />
                  </div>
                )}
              </m.div>
            </nav>
          </m.div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function MobileMenuTopBar({
  platform = null,
  logoHref = "/",
  isActive,
  onClose,
  autoFocus = false,
  ctas,
  goToAppCta,
  inverse,
  offset = 0,
}: {
  isActive: boolean;
  onClose: () => void;
  autoFocus?: boolean;
  offset?: number;
} & Pick<NavigationBarProps, "ctas" | "goToAppCta" | "inverse" | "platform" | "logoHref">) {
  const t = useTranslations("ClientKeys.Globals");
  const buttonRef = useAutoFocus(autoFocus);

  return (
    <div
      className={clsx("tw-absolute tw-inset-x-0 tw-bottom-full", !isActive && "tw-opacity-0")}
      style={{ marginBottom: pxToRem(offset) }}
    >
      <div className='tw-flex tw-items-center tw-gap-x-4 tw-h-16 container'>
        <Logo platform={platform} href={logoHref} inverse={inverse} />
        <CTALinks ctas={ctas} goToAppCta={goToAppCta} inverse={inverse} keepLastOnDesktop className='tw-ml-auto' />
        <button
          ref={buttonRef}
          onClick={onClose}
          aria-label={t("navigation.close")}
          className={clsx(
            "tw-relative tw-rounded-full tw-outline-none tw-p-1.5 -tw-m-1.5 -tw-mr-2.5 focus-visible:tw-outline-focus",
            inverse ? "tw-text-white" : "tw-text-black",
          )}
        >
          <span className='tw-absolute -tw-inset-2.5' />
          <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-6'>
            <path stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' d='m8 16 8-8M8 8l8 8' />
          </svg>
        </button>
      </div>
    </div>
  );
}

function MobileSubNav({
  logoHref,
  label,
  subNavs,
  featured,
  isOpen,
  setIsOpen,
  closeMenu,
  triggerRef,
  isMenuExiting,
  ctas,
  goToAppCta,
  inverse,
  renderTopBar,
  className,
}: Pick<INavigationItem, "label" | "featured"> & { subNavs: NonNullable<INavigationItem["subNavs"]> } & Pick<
    NavigationBarProps,
    "ctas" | "goToAppCta" | "inverse" | "logoHref" | "className"
  > & {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    closeMenu: () => void;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
    isMenuExiting: boolean;
    isSecondary?: boolean;
    renderTopBar?: (context: MobileMenuTopBarContext) => React.ReactNode;
  }) {
  // Whether the subnav is exiting. Used to keep the subnav open until the animation is complete.
  // https://react-spectrum.adobe.com/react-aria/styling.html#framer-motion
  const [isExiting, setIsExiting] = useState(false);

  // Set the open state while also ensuring that the `isExiting` state is updated.
  function toggle(isOpen: boolean) {
    if (isOpen) {
      setIsOpen(true);
    } else {
      setIsExiting(true);
      setIsOpen(false);
    }
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={toggle}>
      <RACButton className='tw-group tw-flex tw-items-center tw-justify-between tw-gap-x-2 tw-w-full tw-text-left tw-pl-4 tw-pr-3 tw-py-3 tw-rounded-xl tw-transition-colors hover:tw-bg-gray-100 tw-text-xs/5 tw-font-medium tw-text-black tw-outline-none focus-visible:tw-outline-focus'>
        {label}
        <svg
          fill='none'
          viewBox='0 0 24 24'
          aria-hidden
          className='tw-size-5 tw-flex-none tw-text-gray-400 tw-transition-colors group-hover:tw-text-black'
        >
          <path
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.8'
            d='m10 16 3.293-3.293a1 1 0 0 0 0-1.414L10 8'
          />
        </svg>
      </RACButton>
      <Popover
        triggerRef={triggerRef}
        placement='bottom right'
        containerPadding={0}
        offset={0}
        className={clsx("tw-h-screen tw-w-full tw-flex", className)}
        isExiting={isExiting || isMenuExiting}
      >
        <Dialog className='tw-relative tw-w-full tw-rounded-[1.25rem] tw-flex'>
          {renderTopBar &&
            renderTopBar({
              logoHref,
              isActive: !isExiting && !isMenuExiting,
              onClose: closeMenu,
              ctas,
              goToAppCta,
              inverse,
              isSubNav: true,
            })}
          <m.div
            className='tw-w-full tw-flex tw-flex-col tw-p-1 -tw-mt-1'
            style={
              !isOpen && isMenuExiting
                ? {
                    maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
                    maskSize: "100% 300%",
                  }
                : {}
            }
            initial={{ maskPosition: "0% 100%", opacity: 0 }}
            animate={
              isOpen
                ? { opacity: 1, maskPosition: "0% 0%" }
                : isMenuExiting
                  ? { opacity: 1, maskPosition: "0% 100%" }
                  : { opacity: 0 }
            }
            onAnimationComplete={isOpen ? undefined : () => setIsExiting(false)}
          >
            <div className='tw-flex-none tw-flex tw-items-center tw-gap-3 tw-p-5 tw-border-b tw-border-gray-100'>
              <BackButton onBack={() => toggle(false)} />
              <div className='tw-type-xs tw-font-medium tw-text-black'>{label}</div>
            </div>
            <ul role='list' className='tw-flex-auto tw-p-6 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-gray-200'>
              {subNavs.map((subNav, subNavIndex) => (
                <li key={subNavIndex} className={clsx(subNavIndex > 0 && "tw-mt-7")}>
                  {subNav.label && <div className='tw-type-2xs tw-font-medium tw-text-gray-500'>{subNav.label}</div>}
                  <ul role='list' className={clsx(subNav.label ? "tw-mt-3" : "-tw-mt-2")}>
                    {subNav.items?.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className='tw-relative tw-py-3.5 tw-px-3 -tw-mx-3 has-[:focus-visible]:tw-outline-focus tw-rounded-xl'
                      >
                        <BaseLink
                          href={item.href}
                          target={item.target}
                          rel={item.rel}
                          className='tw-block tw-type-xs tw-font-medium tw-text-black tw-outline-none'
                        >
                          {item.label}
                          <span className='tw-absolute tw-inset-0' />
                        </BaseLink>
                        {item.description && (
                          <div className='tw-mt-2.5 tw-type-2xs tw-text-gray-500'>{item.description}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            {featured && <FeatureFooter {...featured} />}
          </m.div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  const t = useTranslations("ClientKeys.Globals");
  const buttonRef = useAutoFocus();

  return (
    <button
      ref={buttonRef}
      className='tw-flex-none tw-relative tw-rounded tw-outline-none focus-visible:tw-outline-focus'
      onClick={onBack}
      aria-label={t("back")}
    >
      <span className='tw-absolute -tw-inset-4' />
      <svg width='1.125rem' height='1.125rem' fill='none' viewBox='0 0 18 18' aria-hidden>
        <path
          stroke='currentColor'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='1.5'
          d='M7.5 4.5 3 9l4.5 4.5M3.75 9H15'
        />
      </svg>
    </button>
  );
}

function FeatureFooter({
  link,
  image,
  description,
  ctaLabel,
  isDesktop = false,
}: IFeatureCard & { isDesktop?: boolean }) {
  if (!link || !link.href || !link.label) {
    return null;
  }

  const imageProps = getImageProps(image, { placeholder: true });

  const navLink = (
    <BaseLink href={link.href} className='tw-rounded tw-outline-none'>
      <span className='tw-absolute tw-inset-0' />
      {link.label}
    </BaseLink>
  );

  return (
    <div
      className={clsx(
        "tw-relative tw-w-0 tw-min-w-full tw-border-t tw-border-gray-100 tw-p-5 tw-flex tw-items-center tw-gap-4 tw-min-h-[5.5rem] tw-transition-colors tw-bg-white hover:tw-bg-gray-50 has-[:focus-visible]:tw-outline-focus has-[:focus-visible]:-tw-outline-offset-6",
        isDesktop ? "tw-rounded-b-3xl" : "tw-rounded-b-[1.25rem]",
      )}
    >
      {imageProps && (
        <Image {...imageProps} className='tw-size-12 tw-flex-none tw-object-cover tw-rounded-[0.625rem]' />
      )}
      <div className='tw-flex-auto'>
        <h2 className='tw-type-xs tw-font-medium tw-text-black'>
          {isDesktop ? <NavigationMenu.Link render={navLink} /> : navLink}
        </h2>
        {description && <p className='tw-mt-2.5 tw-type-2xs tw-text-gray-500'>{description}</p>}
      </div>
      {ctaLabel && (
        <div className='tw-flex-none tw-hidden sm:tw-block'>
          <Button asChild variant='elevated' size='md'>
            <div>{ctaLabel}</div>
          </Button>
        </div>
      )}
    </div>
  );
}

function CTALinks({
  ctas,
  buttonSize = "sm",
  buttonGrow = false,
  inverse = false,
  keepLastOnDesktop = false,
  className,
}: {
  buttonGrow?: boolean;
  buttonSize?: "sm" | "md";
  className?: string;
  ctas?: ILink[];
  goToAppCta?: ILink;
  inverse?: boolean;
  keepLastOnDesktop?: boolean;
}) {
  return (
    <div className={clsx("tw-flex tw-items-center tw-gap-x-2", className)}>
      {ctas?.map(({ href, label, ...rest }, index) => (
        <Link
          key={index}
          {...rest}
          asButton
          buttonVariants={{
            size: buttonSize,
            variant: index > 0 ? (inverse ? "elevated" : "primary") : inverse ? "ghost-inverse" : "elevated",
          }}
          className={clsx(
            "min-w-60",
            ctas.length > 1 && index < 1 && keepLastOnDesktop ? "tw-hidden lg:tw-inline-flex" : "",
            buttonGrow ? "tw-grow" : "",
          )}
          href={href}
          showExternalLoading
        >
          {label}
        </Link>
      ))}
    </div>
  );
}

function NavigationMenuContent({ children }: { children: React.ReactNode }) {
  return (
    <NavigationMenu.Content
      style={{ transitionProperty: "opacity, transform, translate" }}
      className={({ activationDirection, transitionStatus }) =>
        clsx(
          "tw-h-full tw-w-max tw-flex tw-flex-col tw-duration-300",
          // No-op transition to prevent the content from being hidden instantly when the menu is closed
          (transitionStatus === "starting" || transitionStatus === "ending") &&
            !activationDirection &&
            "tw-opacity-[.999]",
          // Transition when switching between top-level items
          (transitionStatus === "starting" || transitionStatus === "ending") && activationDirection && "tw-opacity-0",
          transitionStatus === "starting" && activationDirection === "left" && "-tw-translate-x-3",
          transitionStatus === "starting" && activationDirection === "right" && "tw-translate-x-3",
          transitionStatus === "ending" && activationDirection === "left" && "tw-translate-x-3",
          transitionStatus === "ending" && activationDirection === "right" && "-tw-translate-x-3",
        )
      }
    >
      {children}
    </NavigationMenu.Content>
  );
}

function PlatformImage({ src }: { src: StaticImageData }) {
  return (
    <div className='tw-relative tw-size-5 tw-rounded-full tw-overflow-hidden'>
      <Image src={src} alt='' className='tw-absolute tw-inset-0 tw-size-full tw-object-cover' placeholder='blur' />
      <Noise className='tw-opacity-50' />
    </div>
  );
}

function getPlatformLinkImage(id: keyof IPlatformLinks): React.ReactNode {
  if (id === "home") {
    return <LogomarkStatic className='tw-h-3' />;
  }

  const platformLinkImages: Record<Exclude<keyof IPlatformLinks, "home">, StaticImageData> = {
    creative: imgCreative,
    agents: imgAgents,
    api: imgApi,
  };

  return <PlatformImage src={platformLinkImages[id]} />;
}

function Logo({
  platform = null,
  href,
  inverse = false,
  className,
  platformLinks,
  isDesktop = false,
}: {
  platform?: ICmsPlatform;
  href: string;
  inverse?: boolean;
  className?: string;
  platformLinks?: IPlatformLinks;
  isDesktop?: boolean;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const triggerRef = useRef<HTMLElement>(null);
  const [brandMenuIsOpen, setBrandMenuIsOpen] = useState(false);
  const hasPlatformLinks = isDesktop && platformLinks && platform !== null && platform !== "default";

  let LogoComponent = LogoStatic;
  if (platform === "creative") {
    LogoComponent = LogoStaticCreative;
  } else if (platform === "agents") {
    LogoComponent = LogoStaticAgents;
  } else if (platform === "api") {
    LogoComponent = LogoStaticApi;
  }

  function onContextMenu(event: React.MouseEvent) {
    event.preventDefault();
    setBrandMenuIsOpen(true);
  }

  const logoProps = {
    ["aria-label"]: t("elevenlabs"),
    className: clsx(
      "tw-group tw-outline-none focus-visible:tw-outline-focus tw-rounded-full tw-h-9 tw-flex tw-gap-1 tw-items-center tw-px-3 -tw-mx-3",
      inverse ? "tw-text-white" : "tw-text-black",
      className,
    ),
    children: <LogoComponent style={{ height: pxToRem(15) }} />,
    onContextMenu,
  };

  return (
    <>
      {hasPlatformLinks ? (
        <>
          <NavigationMenu.Trigger ref={triggerRef as React.Ref<HTMLButtonElement>} {...logoProps}>
            {logoProps.children}
            {isDesktop && (
              <svg
                fill='none'
                viewBox='0 0 24 24'
                aria-hidden
                className='tw-flex-none tw-size-4 tw-relative tw-top-0.25 -tw-mr-1.5 tw-transition-colors tw-text-gray-500 group-hover:tw-text-black group-aria-expanded:tw-text-black'
              >
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1.5'
                  vectorEffect='non-scaling-stroke'
                  d='m8 10 3.293 3.293a1 1 0 0 0 1.414 0L16 10'
                />
              </svg>
            )}
          </NavigationMenu.Trigger>
          <NavigationMenuContent>
            <ul role='list' className='tw-p-1.5'>
              {(Object.entries(platformLinks) as [keyof IPlatformLinks, IPlatformLink][]).map(([id, link]) => (
                <li key={id}>
                  <NavigationLink
                    href={String(link.href)}
                    className='tw-group tw-h-14 tw-rounded-xl tw-pl-2.5 tw-pr-3 tw-flex tw-items-center tw-gap-3 tw-transition-colors hover:tw-bg-gray-50 tw-outline-none focus-visible:tw-outline-focus'
                  >
                    <div className='tw-size-9 tw-flex-none tw-rounded-lg tw-bg-gray-100 tw-overflow-hidden tw-ring-0.5 tw-ring-inset tw-ring-black/7.5 tw-flex tw-items-center tw-justify-center group-hover:tw-bg-white tw-transition-colors'>
                      {getPlatformLinkImage(id)}
                    </div>
                    <div>
                      <div className='tw-type-sm tw-text-black'>
                        <div className='tw-truncate'>{link.label}</div>
                      </div>
                      <div className='tw-text-gray-500 tw-type-2xs tw-mt-2'>
                        <div className='tw-truncate'>{link.description}</div>
                      </div>
                    </div>
                  </NavigationLink>
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </>
      ) : isDesktop ? (
        <NavigationMenu.Link
          render={<BaseLink ref={triggerRef as React.Ref<HTMLAnchorElement>} {...logoProps} href={href} />}
        />
      ) : (
        <BaseLink ref={triggerRef as React.Ref<HTMLAnchorElement>} {...logoProps} href={href} />
      )}
      <ListBox
        aria-label={t("navigation.brand.menuLabel")}
        placement='bottom start'
        asMenu
        triggerRef={triggerRef}
        isOpen={brandMenuIsOpen}
        onOpenChange={setBrandMenuIsOpen}
        crossOffset={-4}
        sections={[
          {
            id: "logo",
            label: t("navigation.brand.copyLogoLabel"),
            items: [
              {
                id: "logo-download-svg",
                textValue: t("navigation.brand.downloadSvg"),
                link: {
                  href: "https://11labs-nonprd-15f22c1d.s3.eu-west-3.amazonaws.com/0b9cd3e1-9fad-4a5b-b3a0-c96b0a1f1d2b/elevenlabs-logo-black.svg",
                  target: "_blank",
                },
              },
              {
                id: "logo-download-png",
                textValue: t("navigation.brand.downloadPng"),
                link: {
                  href: "https://11labs-nonprd-15f22c1d.s3.eu-west-3.amazonaws.com/0b9cd3e1-9fad-4a5b-b3a0-c96b0a1f1d2b/elevenlabs-logo-black.png",
                  target: "_blank",
                },
              },
            ],
          },
          {
            id: "symbol",
            label: t("navigation.brand.copySymbolLabel"),
            items: [
              {
                id: "symbol-download-svg",
                textValue: t("navigation.brand.downloadSvg"),
                link: {
                  href: "https://11labs-nonprd-15f22c1d.s3.eu-west-3.amazonaws.com/a2ea339b-8b5e-41bb-b706-24eda8a4c9e3/elevenlabs-symbol.svg",
                  target: "_blank",
                },
              },
              {
                id: "symbol-download-png",
                textValue: t("navigation.brand.downloadPng"),
                link: {
                  href: "https://11labs-nonprd-15f22c1d.s3.eu-west-3.amazonaws.com/a2ea339b-8b5e-41bb-b706-24eda8a4c9e3/elevenlabs-symbol.png",
                  target: "_blank",
                },
              },
            ],
          },
          {
            id: "guidelines",
            label: t("navigation.brand.brand"),
            items: [
              {
                id: "brand-guidelines",
                textValue: t("navigation.brand.brandGuidelines"),
                link: { href: "/brand" },
              },
            ],
          },
        ]}
      />
    </>
  );
}

function SkipToContentLink({
  inverse = false,
  label = "Skip to content",
  id = "content",
}: {
  inverse?: boolean;
  label?: string;
  id?: string;
}) {
  return (
    <Button
      asChild
      className='tw-fixed tw-left-[-99999px] tw-top-5 tw-z-[99999] focus-visible:tw-left-5'
      variant={inverse ? "secondary" : "primary"}
      size='md'
    >
      <a href={`#${id}`}>{label}</a>
    </Button>
  );
}

// Used to auto-focus the "close" and "back" buttons in the mobile menu.
// Avoiding other methods like the `autoFocus` prop because it causes weirdness in this case.
function useAutoFocus(enabled: boolean = true) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (enabled) {
      ref.current?.focus();
    }
  }, [enabled]);

  return ref;
}
